import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { PlaceholderBar } from '../../../../components/loading-skeleton/index';

/**
 * The Activity screens while their first answer is on its way. Each one mirrors the layout it stands
 * in for — the same panels, card sizes and row heights — so nothing moves when the data lands.
 */

// Mirrors MetricCard: icon, label, value.
const MetricCardPlaceholder: FC = () => (
  <div className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-white p-3">
    <PlaceholderBar className="size-8 rounded bg-gray-100" />
    <div className="flex flex-col gap-1">
      <PlaceholderBar className="my-1 h-2 w-20 rounded bg-gray-100" />
      <PlaceholderBar className="my-1.5 h-5 w-12 rounded bg-gray-100" />
    </div>
  </div>
);

// Mirrors MetricPanel, with the room or visitor header above the cards when the screen has one.
const MetricPanelPlaceholder: FC<{ cards: number; header?: ReactNode }> = ({ cards, header }) => (
  <div className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
    {header}
    <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
      {Array.from({ length: cards }).map((_, index) => (
        <MetricCardPlaceholder key={index} />
      ))}
    </div>
  </div>
);

// Mirrors the room and visitor header: avatar, name and status, the action on the right.
const SubjectHeaderPlaceholder: FC = () => (
  <div className="flex h-[50px] items-center justify-between gap-4">
    <div className="flex items-center gap-3">
      <PlaceholderBar className="size-12 rounded-full bg-gray-100" />
      <div className="flex flex-col gap-2">
        <PlaceholderBar className="h-5 w-36 rounded bg-gray-100" />
        <PlaceholderBar className="h-3 w-14 rounded bg-gray-100" />
      </div>
    </div>
    <PlaceholderBar className="h-9 w-32 rounded bg-gray-100" />
  </div>
);

// Mirrors EngagementPanel: title and caption over the plotting area.
const ChartPlaceholder: FC = () => (
  <div className="flex flex-col gap-8 rounded-md bg-white p-6 shadow-sm">
    <div className="flex flex-col gap-2 pb-2">
      <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
      <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
    </div>
    <PlaceholderBar className="h-[343px] w-full rounded bg-gray-100" />
  </div>
);

// Mirrors ActivitySection and MostCard: an uppercase title over a white card.
const SectionPlaceholder: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={twMerge(
      'flex min-w-0 flex-1 flex-col gap-3 rounded-md bg-gray-50 px-0.5 pb-0.5 pt-3',
      className,
    )}
  >
    <PlaceholderBar className="mx-4 h-3 w-32 rounded bg-gray-100" />
    <div className="flex flex-1 flex-col rounded-md bg-white p-1 shadow-sm">{children}</div>
  </div>
);

// Every skeleton table shows the same five rows, and so does a page on its way.
export const TABLE_PLACEHOLDER_ROWS = 5;

/** Mirrors the activity tables: a 44px header and 72px rows, the first column led by an icon. */
const TablePlaceholder: FC<{ columns: number }> = ({ columns }) => (
  <div className="flex flex-col">
    <div className="flex h-11 items-center gap-8 border-b border-gray-100 bg-gray-50 px-4">
      {Array.from({ length: columns }).map((_, index) => (
        <PlaceholderBar
          className={twMerge('h-3 flex-1 rounded bg-gray-100', index === 0 && 'flex-[2]')}
          key={index}
        />
      ))}
    </div>
    {Array.from({ length: TABLE_PLACEHOLDER_ROWS }).map((_, row) => (
      <div
        className="flex h-[72px] items-center gap-8 px-4 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-gray-100"
        key={row}
      >
        <div className="flex flex-[2] items-center gap-3">
          <PlaceholderBar className="size-8 shrink-0 rounded-lg bg-gray-100" />
          <PlaceholderBar className="h-3 w-full max-w-40 rounded bg-gray-100" />
        </div>
        {Array.from({ length: columns - 1 }).map((_, index) => (
          <PlaceholderBar className="h-3 flex-1 rounded bg-gray-100" key={index} />
        ))}
      </div>
    ))}
  </div>
);

/** A list tab's table — Data Rooms, Files, Visitors — in its bordered frame. */
export const ActivityTablePlaceholder: FC<{ columns: number }> = ({ columns }) => (
  <div className="flex animate-pulse flex-col p-4">
    <div className="overflow-hidden rounded-md border border-gray-100">
      <TablePlaceholder columns={columns} />
    </div>
  </div>
);

export const ActivityOverviewPlaceholder: FC = () => (
  <div className="flex animate-pulse flex-col gap-4 p-4">
    <MetricPanelPlaceholder cards={6} />
    <ChartPlaceholder />
    <div className="flex flex-wrap gap-3">
      {Array.from({ length: 4 }).map((_, index) => (
        <SectionPlaceholder className="min-w-[240px] rounded-lg px-1 pb-1" key={index}>
          <div className="flex flex-col gap-6 p-3">
            <div className="flex items-center gap-2">
              <PlaceholderBar className="size-10 shrink-0 rounded-[7.5px] bg-gray-100" />
              <div className="flex flex-1 flex-col gap-2">
                <PlaceholderBar className="h-4 w-28 rounded bg-gray-100" />
                <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
              </div>
            </div>
            <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
          </div>
        </SectionPlaceholder>
      ))}
    </div>
  </div>
);

export const ActivityRoomPlaceholder: FC = () => (
  <div className="animate-pulse p-4">
    <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm">
      <MetricPanelPlaceholder cards={7} header={<SubjectHeaderPlaceholder />} />
      <ChartPlaceholder />
      <div className="flex flex-wrap gap-4">
        <SectionPlaceholder className="min-w-[440px]">
          <TablePlaceholder columns={5} />
        </SectionPlaceholder>
        <SectionPlaceholder className="min-w-[440px]">
          <TablePlaceholder columns={5} />
        </SectionPlaceholder>
      </div>
      <SectionPlaceholder>
        <TablePlaceholder columns={8} />
      </SectionPlaceholder>
    </div>
  </div>
);

export const ActivityVisitorPlaceholder: FC = () => (
  <div className="animate-pulse p-4">
    <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm">
      <MetricPanelPlaceholder cards={5} header={<SubjectHeaderPlaceholder />} />
      <div className="flex flex-col gap-4 rounded-md bg-white px-4 pb-4 shadow-sm">
        <div className="flex items-center gap-2 py-4">
          <PlaceholderBar className="size-6 rounded-full bg-gray-100" />
          <PlaceholderBar className="h-4 w-36 rounded bg-gray-100" />
        </div>
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            className="flex h-11 items-center rounded-md border border-gray-100 px-4"
            key={index}
          >
            <PlaceholderBar className="h-3 w-40 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** Mirrors closed timeline days, standing where the next ones are on their way. */
export const ActivityTimelineDaysPlaceholder: FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <div
        aria-hidden
        className="flex h-11 animate-pulse items-center justify-between rounded-md bg-white px-4 ring-1 ring-inset ring-gray-100"
        key={index}
      >
        <PlaceholderBar className="h-3 w-40 rounded bg-gray-100" />
        <PlaceholderBar className="size-5 rounded bg-gray-100" />
      </div>
    ))}
  </>
);
